import React from 'react';
import { useSelector } from 'react-redux';

import { StarGray, StarWhite } from '@travel/icons/ui';
import { useTranslation } from '@travel/traveler-core/hooks';
import { FlatButton } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import ProviderFilterGroup from 'components/ProviderFilterGroup';
import ProviderFilterPrice from 'components/ProviderFilterPrice';

import { getFiltersContractType } from 'store/providerList/selectors';

import { FILTER_GROUP_IDS, PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import { Translate } from 'core/translate';
import type { ProviderFilter } from 'ProviderList-Types';

import type { PriceRangeType } from '../FilterContainer';

import styles from './filterItem.module.scss';

type Props = {
  className?: string;
  /** Object of recent searched filters */
  filter: ProviderFilter;
  /** Number of current selected min price */
  minPrice?: PriceRangeType;
  /** Number of current selected max price */
  maxPrice?: PriceRangeType;
  /** Array of selected star ratings */
  starRating?: Array<string>;
  /** Array of current selected review ratings */
  reviewRating?: Array<string>;
  /** String of current selected feature ids */
  features?: Array<string>;
  /** Callback to be called when checkbox value changed */
  onCheckboxChanged?: (featureId: string, groupId: string) => void;
  /** Callback to be called when selected price changed */
  onPriceRangeChanged?: (min?: number, max?: number) => void;
  /** is in the filterLits not in the wide menu */
  isInFilterList?: boolean;
  /** boolean of current selected free cancellation */
  freeCancellation?: boolean;
};

function FilterItem(props: Props) {
  const { filter } = props;
  const contractType = useSelector(getFiltersContractType);
  const isDirectContract = contractType === PROVIDER_CONTRACT_TYPES.DIRECT;
  const freeCancellationString = useTranslation({
    id: 'Cancellation_Policy_Common.Provider_List.Free_Cancellation',
  });
  let checkboxOptions;

  switch (filter.groupId) {
    case FILTER_GROUP_IDS.REVIEW_SCORE:
      checkboxOptions =
        filter.rating?.map(rating => ({
          ...rating,
          selected: Boolean(props.reviewRating?.includes(String(rating.value))),
          featureId: String(rating.value),
          name: rating.name || '',
          managementName: rating.name || '',
          isObjective: false,
        })) || [];
      break;
    case FILTER_GROUP_IDS.CANCELLATION_POLICY:
      checkboxOptions = [
        {
          ...filter.freeCancellation,
          selected: Boolean(props.freeCancellation),
          featureId: freeCancellationString,
          name: freeCancellationString,
          managementName: freeCancellationString,
        },
      ];
      break;
    default:
      checkboxOptions =
        filter.features?.map(item => ({
          ...item,
          selected:
            Boolean(props.features?.includes(item.featureId)) ||
            Boolean(props.features?.includes(item.managementName || '')),
        })) || [];
      break;
  }

  return (
    <div data-testid="filterItem-wrapper" className={props.className}>
      {filter.groupId === FILTER_GROUP_IDS.PRICE &&
        props.minPrice !== null &&
        props.maxPrice !== null && (
          <ProviderFilterPrice
            shouldHideClear={props.isInFilterList ?? false}
            key="price-range"
            className={cx(styles.filterSection, styles.priceRange)}
            min={filter.min}
            max={filter.max}
            appliedMin={props.minPrice}
            appliedMax={props.maxPrice}
            onValueUpdate={props.onPriceRangeChanged}
          />
        )}

      {filter.groupId === FILTER_GROUP_IDS.RATING && isNotEmptyArray(filter.rating) && (
        <>
          <p className={styles.filterTitle} data-testid="filterItem-rating-title">
            {filter.groupName}
          </p>
          <div
            className={cx(
              styles.filterSection,
              styles.starRatingSection,
              props.isInFilterList && styles.starRatingList,
            )}
            key="star-rating"
          >
            {filter.rating.map(option => {
              const isSelected = props.starRating?.includes(String(option.value));

              return (
                <FlatButton
                  data-testid={`filterItem-rating-${option.value}-button`}
                  labelClassName={styles.label}
                  className={cx(styles.starRatingButton, isSelected && styles.selected)}
                  classType={isSelected ? 'primary' : 'secondary'}
                  key={option.value}
                  type="button"
                  onClick={() => props.onCheckboxChanged?.(filter.groupId, String(option.value))}
                >
                  {option.value}{' '}
                  {isSelected ? (
                    <StarWhite className={styles.icon} size={14} />
                  ) : (
                    <StarGray className={styles.icon} size={14} />
                  )}
                </FlatButton>
              );
            })}
          </div>
        </>
      )}

      {filter.groupId === FILTER_GROUP_IDS.REVIEW_SCORE && isNotEmptyArray(filter.rating) && (
        <div className={cx(styles.filterSection, styles.selectionsContainer)} key="review-rating">
          <ProviderFilterGroup
            id={filter.groupId}
            name="review-rating"
            title={filter.groupName}
            options={checkboxOptions}
            onChange={props.onCheckboxChanged}
            className={styles.container}
            optionTextClassName={styles.optionTextClassName}
            shouldDisplayHits={isDirectContract}
          />
        </div>
      )}

      {filter.groupId === FILTER_GROUP_IDS.CANCELLATION_POLICY && (
        <div
          className={cx(styles.filterSection, styles.selectionsContainer)}
          key="cancellation-policy"
        >
          <ProviderFilterGroup
            id={filter.groupId}
            name="cancellation-policy"
            title={
              <Translate id="Cancellation_Policy_Common.Cancellation_Policy_Modal.Section_Title" />
            }
            options={checkboxOptions}
            onChange={props.onCheckboxChanged}
            className={styles.container}
            optionTextClassName={styles.optionTextClassName}
            shouldDisplayHits={isDirectContract}
          />
        </div>
      )}

      {isNotEmptyArray(filter.features) && (
        <div className={cx(styles.filterSection, styles.selectionsContainer)}>
          <ProviderFilterGroup
            id={filter.groupId}
            title={filter.groupName}
            options={checkboxOptions}
            onChange={props.onCheckboxChanged}
            className={styles.container}
            optionTextClassName={styles.optionTextClassName}
            shouldDisplayHits={isDirectContract}
          />
        </div>
      )}
    </div>
  );
}

export default FilterItem;
